import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../services/help';
import { serviceProviderService } from '../../services';
import { ServiceProviderDetail } from '../../types';
import Tooltip from '@mui/material/Tooltip';
import ContextMenu, { MenuAction } from './ContextMenu';
import { IMAGES } from '../../constants/assets';
import { POLLING_CONFIG } from '../../config/polling';
import UniversalBadge from './UniversalBadge';
import { refreshAll } from '../../services/refreshService';
import { createServiceZipFromFolders, validateServiceFolderStructure, filterSystemFiles } from '../../utils/zip';
import { dragDropStyles } from '../../styles/theme';
import AnalysisCostDialog from './AnalysisCostDialog';
import { useApiAnalysisCost } from '../../hooks/useApiAnalysisCost';
import { useCreditInfo } from '../../hooks/useCreditInfo';
import { useTranslation } from 'react-i18next';
import { useUserRole } from '../../hooks/useUserRole';
import { adminService } from '../../services/admin.service';

export interface Contract {
  id: string;
  title: string;
  status: string;
  // Optional: Roh-Status-Code, falls vom Aufrufer mitgegeben (z.B. 'open')
  statusCode?: string;
  exitCode?: number;
  note: string;
  createdAt: string;
  framework_id?: string;
  type: 'pdf' | 'docx' | 'txt';
  documentsCount?: number;
  isCritical?: boolean;
}

interface ContractTableProps {
  contracts: Contract[];
  selectedItems: string[];
  editingContractId: string | null;
  editingContractTitle: string;
  selectedRowId: string | null;
  providerId?: string;
  enablePolling?: boolean;
  pollingInterval?: number;
  enableFolderDragDrop?: boolean; // Neue Prop für Drag-and-Drop
  onSelectItem: (event: React.ChangeEvent<HTMLInputElement>, id: string) => void;
  onSelectAll: (event: React.ChangeEvent<HTMLInputElement>, contracts: Contract[]) => void;
  onContextMenu?: (event: React.MouseEvent, contractId: string, status: string) => void;
  onRename: (contractId: string) => void;
  onSaveContractTitle: (contractId: string) => void;
  onCancelContractEdit: () => void;
  setEditingContractTitle: (title: string) => void;
  onRowClick?: (contractId: string) => void;
  onContractsUpdate?: (contracts: Contract[]) => void;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void; // Neue Prop für Erfolgs-Nachrichten
  // Context Menu Handlers
  onAnalyze?: (contractId: string) => void;
  onCancelAnalysis?: (contractId: string) => void;
  onDeleteContract?: (contractId: string) => void;
  onDownloadResults?: (contractId: string) => void;
  onDownloadAddendum?: (contractId: string) => void;
}

// Status Übersetzungen - wird jetzt in der Komponente mit t() verwendet

// Hilfsfunktion: Prüft ob Status eine aktive Bearbeitung darstellt
const isProcessingStatus = (status: string): boolean => {
  return status === 'pending' || status === 'analyzing' || status.startsWith('qs_');
};

const ContractTable: React.FC<ContractTableProps> = ({
  contracts,
  selectedItems,
  editingContractId,
  editingContractTitle,
  selectedRowId,
  providerId,
  enablePolling = POLLING_CONFIG.ENABLE_POLLING,
  pollingInterval = POLLING_CONFIG.GLOBAL_INTERVAL,
  enableFolderDragDrop = true,
  onSelectItem,
  onSelectAll,
  onContextMenu,
  onRename,
  onSaveContractTitle,
  onCancelContractEdit,
  setEditingContractTitle,
  onRowClick,
  onContractsUpdate,
  onError,
  onSuccess, // Neue Prop
  // Context Menu Handlers
  onAnalyze,
  onCancelAnalysis,
  onDeleteContract,
  onDownloadResults,
  onDownloadAddendum,
}) => {
  const { t } = useTranslation();
  const { role } = useUserRole();

  // Status Übersetzungen
  const getStatusText = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'open':
        return t('common:status.open');
      case 'pending':
        return t('common:status.pending');
      case 'analyzing':
        return t('common:status.analyzing');
      case 'complete':
        return t('common:status.complete');
      case 'failed':
        return t('common:status.failed');
      default:
        return status.startsWith('qs_') ? t('common:status.qsInProgress') : status;
    }
  };
  const isOpenStatus = (statusOrCode?: string): boolean => {
    if (!statusOrCode) return false;
    const s = statusOrCode.toLowerCase();
    return s === 'open';
  };
  const [pollingContracts, setPollingContracts] = useState<Contract[]>(contracts);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    contractId: string;
    status: string;
  } | null>(null);

  // Drag-and-Drop State
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [showCostDialog, setShowCostDialog] = useState(false);
  const [pendingAnalysisContractId, setPendingAnalysisContractId] = useState<string | null>(null);

  // 📊 Credit-Information laden
  const creditInfo = useCreditInfo();

  // Hook auf Komponentenebene
  const navigate = useNavigate();

  // Drag-and-Drop Handlers  
  const handleFolderUpload = useCallback(async (files: File[]) => {
    if (!enableFolderDragDrop || !providerId || files.length === 0) return;

    setIsProcessingUpload(true);

    try {
      // Prüfe auf ZIP-Dateien und lehne sie ab
      const hasZipFiles = files.some(file =>
        file.type === 'application/zip' ||
        file.type === 'application/x-zip-compressed' ||
        file.name.toLowerCase().endsWith('.zip')
      );

      if (hasZipFiles) {
        onError?.('ZIP-Dateien sind hier nicht erlaubt. Bitte verwenden Sie den "Mehrere Dienstleistungen hochladen"-Button für ZIP-Dateien.');
        return;
      }

      // Filtere System-Dateien automatisch heraus
      const cleanFiles = filterSystemFiles(files);

      if (cleanFiles.length === 0) {
        onError?.('Keine gültigen Dateien gefunden (nur System-Dateien vorhanden)');
        return;
      }

      if (cleanFiles.length < files.length) {
        const filteredCount = files.length - cleanFiles.length;
        console.log(`🗑️ ${filteredCount} System-Datei(en) automatisch herausgefiltert`);
      }

      // Validiere Ordner-Struktur
      const validation = validateServiceFolderStructure(cleanFiles);

      if (!validation.isValid) {
        onError?.(validation.errors.join('\n'));
        return;
      }

      // Zeige Info über gefundene Services
      const serviceCount = validation.serviceNames.length;
      onSuccess?.(`${serviceCount} Dienstleistung${serviceCount > 1 ? 'en' : ''} gefunden: ${validation.serviceNames.join(', ')}`);

      // Erstelle ZIP-Datei aus Ordner-Struktur (bereits gefilterte Dateien verwenden)
      const zipFile = await createServiceZipFromFolders(cleanFiles, 'dienstleistungen.zip');

      // Sende an bestehende API
      const result = await serviceProviderService.bulkUploadContracts(providerId, zipFile);

      if (result.status === 'success') {
        onSuccess?.(result.message);

        // Aktualisiere Verträge
        const freshProviderData = await serviceProviderService.getServiceProviderDetails(providerId);

        if (!freshProviderData) {
          onError?.('Dienstleister nicht mehr vorhanden');
          return;
        }

        const freshContracts: Contract[] = freshProviderData.contractPackages?.map(contractPackage => ({
          id: contractPackage._id,
          title: contractPackage.title,
          status: contractPackage.status,
          statusCode: contractPackage.status,
          note: contractPackage.note || '',
          createdAt: contractPackage.createdAt,
          type: (contractPackage.documents?.[0]?.type || 'pdf') as 'pdf' | 'docx' | 'txt',
          documentsCount: contractPackage.documents?.length || 0,
          isCritical: contractPackage.isCritical,
        })) || [];

        setPollingContracts(freshContracts);
        onContractsUpdate?.(freshContracts);
        refreshAll();
      } else {
        onError?.(result.message);
      }
    } catch (error: any) {
      console.error('Folder upload error:', error);

      // Prüfe auf spezifische API-Fehlermeldungen
      if (error?.response?.data?.message) {
        onError?.(error.response.data.message);
      } else if (error?.response?.data?.status === 'error' && error?.response?.data?.message) {
        onError?.(error.response.data.message);
      } else if (error?.message) {
        onError?.(error.message);
      } else {
        onError?.('Fehler beim Hochladen der Ordner');
      }
    } finally {
      setIsProcessingUpload(false);
    }
  }, [enableFolderDragDrop, providerId, onError, onSuccess, onContractsUpdate]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!enableFolderDragDrop) return;

    e.preventDefault();
    e.stopPropagation();

    // Prüfe ob Ordner/Dateien gedraggt werden
    const hasFiles = e.dataTransfer.types.includes('Files');
    if (hasFiles) {
      setIsDragOver(true);
    }
  }, [enableFolderDragDrop]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!enableFolderDragDrop) return;

    e.preventDefault();
    e.stopPropagation();

    // Nur hiding wenn wir den Container wirklich verlassen
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  }, [enableFolderDragDrop]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    if (!enableFolderDragDrop) return;

    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    console.log('🎯 Drag & Drop Event ausgelöst');

    const files: File[] = [];

    // Verarbeite DataTransfer Items (für bessere Ordner-Unterstützung)
    if (e.dataTransfer.items) {
      const items = Array.from(e.dataTransfer.items);
      console.log('📋 DataTransfer Items:', items.length);

      // Sammle alle Promises für parallele Verarbeitung
      const promises: Promise<void>[] = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const entry = item.webkitGetAsEntry?.() || null;

          if (entry) {
            console.log(`📁 Entry ${i + 1}: ${entry.name} (isDirectory: ${entry.isDirectory})`);

            if (entry.isDirectory) {
              // Ordner: Rekursiv alle Dateien sammeln (als Promise)
              console.log(`📂 Verarbeite Ordner ${i + 1}: ${entry.name}`);

              const promise = readDirectoryRecursive(entry as FileSystemDirectoryEntry)
                .then(dirFiles => {
                  console.log(`📄 Dateien in ${entry.name}:`, dirFiles.length);
                  files.push(...dirFiles);
                });
              promises.push(promise);

            } else {
              // Einzelne Datei
              const file = item.getAsFile();
              if (file) {
                console.log(`📄 Einzelne Datei ${i + 1}: ${file.name} (type: ${file.type})`);
                files.push(file);
              }
            }
          } else {
            // Fallback für ältere Browser
            const file = item.getAsFile();
            if (file) {
              console.log(`📄 Fallback Datei ${i + 1}: ${file.name}`);
              files.push(file);
            }
          }
        } else {
          console.log(`⚠️ Item ${i + 1} ist kein File (kind: ${item.kind})`);
        }
      }

      // Warte auf alle Ordner-Verarbeitungen
      if (promises.length > 0) {
        console.log(`⏳ Warte auf ${promises.length} Ordner-Verarbeitungen...`);
        await Promise.all(promises);
      }

    } else {
      // Fallback für DataTransfer.files
      const fileList = Array.from(e.dataTransfer.files);
      console.log('📋 DataTransfer Files Fallback:', fileList.length);
      files.push(...fileList);
    }

    console.log('📦 Gesammelte Dateien insgesamt:', files.length);
    files.forEach((file, index) => {
      console.log(`${index + 1}. ${file.name} (type: ${file.type}, webkitRelativePath: "${(file as any).webkitRelativePath || 'nicht gesetzt'}")`);
    });

    if (files.length > 0) {
      await handleFolderUpload(files);
    }
  }, [enableFolderDragDrop, handleFolderUpload]);

  // Hilfsfunktion für rekursives Lesen von Ordnern
  const readDirectoryRecursive = useCallback(async (directoryEntry: FileSystemDirectoryEntry): Promise<File[]> => {
    const files: File[] = [];

    return new Promise((resolve) => {
      const reader = directoryEntry.createReader();

      const readEntries = () => {
        reader.readEntries(async (entries) => {
          if (entries.length === 0) {
            resolve(files);
            return;
          }

          for (const entry of entries) {
            if (entry.isFile) {
              const file = await new Promise<File>((fileResolve) => {
                (entry as FileSystemFileEntry).file((file) => {
                  // Setze webkitRelativePath manuell für korrekte Struktur
                  Object.defineProperty(file, 'webkitRelativePath', {
                    value: entry.fullPath.substring(1), // Entferne führenden Slash
                    writable: false
                  });
                  fileResolve(file);
                });
              });
              files.push(file);
            } else if (entry.isDirectory) {
              const subDirFiles = await readDirectoryRecursive(entry as FileSystemDirectoryEntry);
              files.push(...subDirFiles);
            }
          }

          readEntries(); // Weiterlesen für große Ordner
        });
      };

      readEntries();
    });
  }, []);

  // Kontextmenü Funktionen
  const handleContextMenu = (event: React.MouseEvent, contractId: string, status: string) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      contractId,
      status
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleResetStatus = useCallback(async (contractId: string) => {
    try {
      const result = await adminService.resetContractPackageStatus(contractId);
      if (result.status === 'success') {
        onSuccess?.(t('providers:messages.resetStatusSuccess'));
        handleCloseContextMenu();

        // Refresh data
        if (providerId) {
          const freshProviderData = await serviceProviderService.getServiceProviderDetails(providerId);
          if (freshProviderData) {
            const freshContracts: Contract[] = freshProviderData.contractPackages?.map(contractPackage => ({
              id: contractPackage._id,
              title: contractPackage.title,
              status: contractPackage.status,
              statusCode: contractPackage.status,
              exitCode: (contractPackage as any).exitCode,
              note: contractPackage.note || '',
              createdAt: contractPackage.createdAt,
              type: (contractPackage.documents?.[0]?.type || 'pdf') as 'pdf' | 'docx' | 'txt',
              documentsCount: contractPackage.documents?.length || 0,
              isCritical: contractPackage.isCritical || false
            })) || [];
            setPollingContracts(freshContracts);
            onContractsUpdate?.(freshContracts);
          }
        }
        refreshAll();
      } else {
        onError?.(result.message || t('providers:messages.resetStatusError'));
      }
    } catch (error: any) {
      console.error('Reset status error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || t('providers:messages.resetStatusError');
      onError?.(errorMessage);
    }
  }, [providerId, onSuccess, onError, onContractsUpdate, t]);

  const handleDownloadJson = useCallback(async (contractId: string) => {
    try {
      await adminService.downloadContractPackageJson(contractId);
      onSuccess?.(t('providers:messages.downloadJsonSuccess'));
      handleCloseContextMenu();
    } catch (error: any) {
      console.error('Download JSON error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || t('providers:messages.downloadJsonError');
      onError?.(errorMessage);
    }
  }, [onSuccess, onError, t]);

  const getContextMenuActions = (contractId: string): MenuAction[] => {
    // Finde den aktuellen Vertrag um den aktuellen Status zu bekommen
    const contract = pollingContracts.find(c => c.id === contractId);
    // Verwende statusCode (roher Backend-Status) falls vorhanden, sonst status
    const currentStatus = (contract?.statusCode || contract?.status)?.toLowerCase() || '';
    return [
      {
        label: t('providers:contextMenu.open'),
        icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt={t('providers:contextMenu.open')} />,
        onClick: () => {
          navigate(`/dienstleister/${providerId}/dienstleistung/${contractId}`);
        }
      },
      {
        label: t('providers:contextMenu.rename'),
        icon: <img src={IMAGES.EDIT} alt={t('providers:contextMenu.rename')} />,
        onClick: () => {
          onRename(contractId);
          handleCloseContextMenu();
        },
        disabled: currentStatus !== 'open'
      },
      {
        label: t('providers:contextMenu.delete'),
        color: '#FF0004',
        icon: <img src={IMAGES.DELETE} alt={t('providers:contextMenu.delete')} className="preserve-color" />,
        onClick: () => {
          onDeleteContract?.(contractId);
          handleCloseContextMenu();
        },
        disabled: currentStatus === 'pending' || currentStatus === 'analyzing' || currentStatus === 'qs_processing'
      },
      {
        label: t('providers:contextMenu.downloadResults'),
        icon: <img src={IMAGES.DOWNLOAD} alt={t('providers:contextMenu.downloadResults')} />,
        onClick: () => {
          onDownloadResults?.(contractId);
          handleCloseContextMenu();
        },
        disabled: currentStatus !== 'complete'
      },
      //   {
      //    label: 'Ergänzungsvereinbarung herunterladen',
      //    icon: <img src={IMAGES.DOWNLOAD} alt="Download" />,
      //    onClick: () => {
      //      onDownloadAddendum?.(contractId);
      //      handleCloseContextMenu();
      //    },
      //    disabled: currentStatus !== 'complete'
      //  },
      {
        label: t('providers:contextMenu.analyze'),
        icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt={t('providers:contextMenu.analyze')} />,
        onClick: () => {
          setPendingAnalysisContractId(contractId);
          setShowCostDialog(true);
          handleCloseContextMenu();
        },
        disabled: currentStatus === 'pending' || currentStatus === 'analyzing' || currentStatus === 'qs_processing' || currentStatus === 'complete' || (contract?.documentsCount || 0) === 0,
        tooltip: (contract?.documentsCount || 0) === 0 ? t('providers:service.noDocuments') : undefined
      },
      {
        label: t('providers:contextMenu.cancelAnalysis'),
        icon: <CloseIcon sx={{ fontSize: '1rem', }} />,
        onClick: () => {
          onCancelAnalysis?.(contractId);
          handleCloseContextMenu();
        },
        disabled: currentStatus !== 'pending' && currentStatus !== 'analyzing'
        // QS-Status können nicht abgebrochen werden - werden automatisch durch disabled Bedingung ausgeschlossen
      },
      ...(role === 'admin' ? [
        {
          label: t('providers:contextMenu.downloadJson'),
          icon: <img src={IMAGES.DOWNLOAD} alt={t('providers:contextMenu.downloadJson')} />,
          onClick: () => {
            handleDownloadJson(contractId);
          },
          disabled: currentStatus !== 'complete'
        },
        {
          label: t('providers:contextMenu.resetStatus'),
          icon: <img src={IMAGES.EDIT} alt={t('providers:contextMenu.resetStatus')} />,
          onClick: () => {
            handleResetStatus(contractId);
          }
        }
      ] : [])
    ];
  };

  // Auto-Polling Logic
  useEffect(() => {
    if (!enablePolling || !providerId) return;

    const pollData = async () => {
      try {
        const freshProviderData: ServiceProviderDetail = await serviceProviderService.getServiceProviderDetails(providerId);

        // Prüfe ob Provider-Daten vorhanden sind
        if (!freshProviderData) {
          onError?.('Dienstleister nicht mehr vorhanden');
          return;
        }

        // Convert to Contract format for comparison
        const freshContracts: Contract[] = freshProviderData.contractPackages?.map(contractPackage => ({
          id: contractPackage._id,
          title: contractPackage.title,
          status: contractPackage.status,
          statusCode: contractPackage.status,
          exitCode: (contractPackage as any).exitCode,
          note: contractPackage.note || '',
          createdAt: contractPackage.createdAt,
          type: (contractPackage.documents?.[0]?.type || 'pdf') as 'pdf' | 'docx' | 'txt',
          documentsCount: contractPackage.documents?.length || 0,
          isCritical: contractPackage.isCritical,
        })) || freshProviderData.individualContracts?.map(contract => ({
          id: contract._id,
          title: contract.title,
          status: contract.status,
          statusCode: contract.status,
          exitCode: (contract as any).exitCode,
          note: contract.note || '',
          createdAt: contract.uploadDate,
          type: contract.type,
          documentsCount: contract.documents?.length || 0
        })) || [];

        // Vergleiche nur Status-Änderungen, um unnötige Re-renders zu vermeiden
        const hasStatusChanges = freshContracts.some((freshContract, index) => {
          const currentContract = pollingContracts[index];
          return currentContract && freshContract.status !== currentContract.status;
        });

        if (hasStatusChanges || freshContracts.length !== pollingContracts.length) {
          setPollingContracts(freshContracts);
          onContractsUpdate?.(freshContracts);
        }
      } catch (error: any) {
        console.error('Contract polling error:', error);

        // Prüfe auf spezifische API-Fehlermeldungen
        if (error?.response?.data?.message) {
          onError?.(error.response.data.message);
        } else if (error?.response?.data?.status === 'error' && error?.response?.data?.message) {
          onError?.(error.response.data.message);
        } else if (error?.message) {
          onError?.(error.message);
        } else {
          onError?.('Fehler beim Aktualisieren der Vertragsdaten');
        }
      }
    };

    // Registriere bei globalem Polling-Manager
    const { PollingManager } = require('../../config/polling');
    const pollingManager = PollingManager.getInstance();

    pollingManager.register('contracts', pollData);

    // Legacy-Fallback (falls globales System deaktiviert)
    const legacyInterval = !POLLING_CONFIG.ENABLE_POLLING ? setInterval(pollData, pollingInterval) : null;

    return () => {
      pollingManager.unregister('contracts');

      if (legacyInterval) {
        clearInterval(legacyInterval);
      }
    };
  }, [enablePolling, pollingInterval, providerId, pollingContracts, onContractsUpdate, onError]);

  // Update internal state when external contracts change
  useEffect(() => {
    setPollingContracts(contracts);
  }, [contracts]);

  // Use polling data if polling is enabled, otherwise use prop data
  const activeContracts = enablePolling ? pollingContracts : contracts;

  // API-basierte Kostenschätzung für einzelne Analyse
  const costEstimation = useApiAnalysisCost({
    analysisType: 'single',
    providerId: providerId || '',
    contractId: pendingAnalysisContractId || ''
  });

  // Trigger Kostenschätzung wenn Dialog geöffnet wird
  useEffect(() => {
    if (showCostDialog && providerId && pendingAnalysisContractId) {
      costEstimation.refetch();
    }
  }, [showCostDialog, providerId, pendingAnalysisContractId, costEstimation.refetch]);

  return (
    <TableContainer
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        minHeight: '200px',
        ...dragDropStyles.container(
          isDragOver && enableFolderDragDrop,
          dragDropStyles.messages.foldersForServices
        ),
        ...(isProcessingUpload && dragDropStyles.processing)
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={selectedItems.length === activeContracts.length && activeContracts.length > 0}
                onChange={(e) => onSelectAll(e, activeContracts)}
                inputProps={{ 'data-testid': 'contracts-table-select-all-checkbox' } as any}
                sx={{ padding: '0.5rem' }}
              />
            </TableCell>
            <TableCell data-testid="contracts-table-header-title">{t('providers:table.title')}</TableCell>
            <TableCell data-testid="contracts-table-header-status">{t('providers:table.status')}</TableCell>
            <TableCell data-testid="contracts-table-header-criticality">{t('providers:table.criticality')}</TableCell>
            <TableCell data-testid="contracts-table-header-note">{t('providers:table.note')}</TableCell>
            <TableCell align="center" data-testid="contracts-table-header-documents">{t('providers:table.documents')}</TableCell>
            <TableCell data-testid="contracts-table-header-modified">{t('providers:table.createdAt')}</TableCell>
            <TableCell align="right" data-testid="contracts-table-header-action">{t('providers:table.action')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {activeContracts.map((vertrag) => (
            <TableRow
              key={vertrag.id}
              hover
              onContextMenu={(e) => handleContextMenu(e, vertrag.id, vertrag.status)}
              onClick={() => onRowClick && onRowClick(vertrag.id)}
              data-testid={`contracts-table-row-${vertrag.id}`}
              sx={{
                cursor: 'pointer',
                height: '4.25rem',

                bgcolor: selectedRowId === vertrag.id ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                transition: enablePolling ? 'background-color 0.2s ease' : 'none',
                '&:hover': {
                  '.edit-icon': {
                    opacity: 1,
                  },
                  bgcolor: selectedRowId === vertrag.id ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedItems.includes(vertrag.id)}
                  onChange={(e) => onSelectItem(e, vertrag.id)}
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{ 'data-testid': `contracts-table-row-${vertrag.id}-checkbox` } as any}
                  sx={{
                    padding: '0.5rem',
                    color: 'primary.blue',
                    '&.Mui-checked': {
                      color: 'primary.blue',
                    }
                  }}
                />
              </TableCell>
              <TableCell>
                {editingContractId === vertrag.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      value={editingContractTitle}
                      onChange={(e) => setEditingContractTitle(e.target.value)}
                      sx={{
                        fontSize: '1rem',
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          color: 'primary.dark',
                          padding: '0.5rem'
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          onSaveContractTitle(vertrag.id);
                        } else if (e.key === 'Escape') {
                          onCancelContractEdit();
                        }
                      }}
                      data-testid={`contracts-table-row-${vertrag.id}-title-edit-input`}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSaveContractTitle(vertrag.id);
                      }}
                      data-testid={`contracts-table-row-${vertrag.id}-title-save-button`}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancelContractEdit();
                      }}
                      data-testid={`contracts-table-row-${vertrag.id}-title-cancel-button`}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      //p: '1rem',
                      minWidth: 0,
                      maxWidth: '250px'
                    }}
                  >
                    <Tooltip title={vertrag.title} arrow>
                      <Box
                        sx={{
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          maxWidth: '250px',
                          lineHeight: 1.2,
                          maxHeight: '2.4em', // 2 lines * 1.2 line-height
                          minWidth: 0,
                          flex: 1
                        }}
                      >
                        {vertrag.title}
                      </Box>
                    </Tooltip>
                    <EditIcon
                      className="edit-icon"
                      sx={{
                        fontSize: '1rem',
                        opacity: 0,
                        cursor: isOpenStatus(vertrag.statusCode || vertrag.status) ? 'pointer' : 'default',
                        color: isOpenStatus(vertrag.statusCode || vertrag.status) ? 'primary.blue' : 'text.disabled',
                        transition: 'opacity 0.2s'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isOpenStatus(vertrag.statusCode || vertrag.status)) return;
                        onRename(vertrag.id);
                      }}
                      data-testid={`contracts-table-row-${vertrag.id}-title-edit-button`}
                    />
                  </Box>
                )}
              </TableCell>
              <TableCell>
                {(vertrag.statusCode || vertrag.status).toLowerCase() === 'failed' && vertrag.exitCode === 3 ? (
                  <Tooltip title="Kontextlimit überschritten (max. 170.000 Tokens)">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      <UniversalBadge type="status" value={vertrag.statusCode || vertrag.status} data-testid={`contracts-table-row-${vertrag.id}-status-badge`} />
                    </Box>
                  </Tooltip>
                ) : (
                  <UniversalBadge type="status" value={vertrag.statusCode || vertrag.status} data-testid={`contracts-table-row-${vertrag.id}-status-badge`} />
                )}
              </TableCell>
              <TableCell>
                {vertrag.isCritical !== undefined && (
                  <UniversalBadge type="criticality" value={vertrag.isCritical} data-testid={`contracts-table-row-${vertrag.id}-criticality-badge`} />
                )}
              </TableCell>
              <TableCell>
                <Tooltip title={vertrag.note || t('providers:table.noNote')} arrow>
                  <Box sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    maxWidth: '250px',
                    lineHeight: 1.2,
                    maxHeight: '2.4em' // 2 lines * 1.2 line-height
                  }}>
                    {vertrag.note || '-'}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                {vertrag.documentsCount || 0}
              </TableCell>
              <TableCell>
                {formatDate(vertrag.createdAt)}
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContextMenu(e, vertrag.id, vertrag.status);
                  }}
                  data-testid={`contracts-table-row-${vertrag.id}-menu-button`}
                >
                  <MoreVertIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Drag-and-Drop Hinweis für leere Tabelle */}
      {enableFolderDragDrop && activeContracts.length === 0 && (
        <Box sx={{
          //position: 'absolute',
          textAlign: 'center',
          color: 'text.secondary',
          pointerEvents: 'none',
          zIndex: 1
        }}>
          <Typography variant="h6" sx={{ my: 2, fontSize: '1.1rem' }}>
            Noch keine Dienstleistungen vorhanden.
          </Typography>
          <Typography variant="body2">
            Fügen Sie Ihre erste Dienstleistung hinzu.
          </Typography>
        </Box>
      )}

      {/* Processing Overlay */}
      {isProcessingUpload && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              📦 Verarbeite Dienstleistungen...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ihre Ordner werden gezippt und hochgeladen
            </Typography>
          </Box>
        </Box>
      )}

      {/* Kontextmenü */}
      <ContextMenu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        mouseX={contextMenu?.mouseX || 0}
        mouseY={contextMenu?.mouseY || 0}
        actions={contextMenu ? getContextMenuActions(contextMenu.contractId) : []}
      />

      {/* Kostenbestätigung Dialog */}
      <AnalysisCostDialog
        open={showCostDialog}
        onClose={() => {
          setShowCostDialog(false);
          setPendingAnalysisContractId(null);
        }}
        onConfirm={() => {
          setShowCostDialog(false);
          if (pendingAnalysisContractId) {
            onAnalyze?.(pendingAnalysisContractId);
            refreshAll();
          }
          setPendingAnalysisContractId(null);
        }}
        analysisType="single"
        contractCount={1}
        loading={costEstimation.loading}
        error={costEstimation.error}
        apiData={costEstimation.data ? {
          totalAnalyzableContracts: costEstimation.data.totalAnalyzableContracts,
          breakdown: costEstimation.data.breakdown,
          details: costEstimation.data.details
        } : null}
        creditInfo={creditInfo.isUnlimited ? null : {
          availableCredits: creditInfo.availableCredits,
          requiredCredits: costEstimation.data?.totalAnalyzableContracts ?? 0,
          remainingAfter: creditInfo.availableCredits !== null
            ? creditInfo.availableCredits - (costEstimation.data?.totalAnalyzableContracts ?? 1)
            : null
        }}
      />
    </TableContainer>
  );
};

export default ContractTable; 